
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const DataDeletionForm = () => {
  const [reason, setReason] = useState('');
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // First sign out the user
      await supabase.auth.signOut();

      // Then delete all user data (same as profile delete account)
      const { error: deleteError } = await supabase.rpc('delete_user_data');
      
      if (deleteError) throw deleteError;

      toast({
        title: "Account deleted",
        description: "Your account and all data have been successfully deleted.",
        duration: 5000,
      });
      
      // Navigate to auth page
      navigate('/auth');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast({
        title: "Error deleting account",
        description: "Please try again later or contact support.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Data Deletion Request</CardTitle>
        <CardDescription>
          Request deletion of your personal data. Under GDPR "Right to be Forgotten" and CCPA, you can request deletion of your personal information.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h5 className="text-sm font-medium text-red-900 mb-2">⚠️ Important Notice</h5>
          <p className="text-xs text-red-800">
            This action will permanently delete all your data including notes, groups, and account information. 
            This cannot be undone. Please consider exporting your data first.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="reason" className="text-sm font-medium block mb-2">
              Reason for deletion (optional)
            </label>
            <Textarea
              id="reason"
              placeholder="Please let us know why you're requesting data deletion..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="confirm"
              checked={confirmDeletion}
              onCheckedChange={(checked) => setConfirmDeletion(checked as boolean)}
            />
            <label htmlFor="confirm" className="text-sm">
              I understand that this action is irreversible and will permanently delete all my data
            </label>
          </div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg">
          <h5 className="text-sm font-medium text-blue-900 mb-2">What data will be deleted?</h5>
          <ul className="text-xs text-blue-800 space-y-1">
            <li>• Your user profile and account information</li>
            <li>• All notes and their content</li>
            <li>• Groups and organizational data</li>
            <li>• Settings and preferences</li>
            <li>• Activity logs and usage data</li>
            <li>• Any uploaded files or images</li>
          </ul>
        </div>

        <div className="bg-yellow-50 p-4 rounded-lg">
          <h5 className="text-sm font-medium text-yellow-900 mb-2">Legal Retention</h5>
          <p className="text-xs text-yellow-800">
            Some data may be retained for legal compliance purposes (e.g., financial records, security logs) 
            as permitted by law. We'll delete all personal data that we're legally allowed to delete.
          </p>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button 
              variant="destructive" 
              disabled={!confirmDeletion || isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting Account...
                </>
              ) : (
                "Delete Account & All Data"
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete your account and all your personal data immediately. 
                This action cannot be undone. All your notes, groups, and account information will be lost forever.
                <ul className="list-disc list-inside mt-2">
                  <li>All your notes and their content</li>
                  <li>Your profile information</li>
                  <li>Your groups and shared content</li>
                  <li>Any stored files or images</li>
                  <li>All your application settings</li>
                </ul>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleSubmit} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete Account Permanently"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="text-xs text-gray-500">
          <p>
            This request is processed in accordance with Article 17 of the GDPR (Right to Erasure) 
            and Section 1798.105 of the CCPA. If you have any questions, please contact our privacy team at hello@mindnote.online.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default DataDeletionForm;
